/**
 * `<caos-message-stream>` — the scrolling list a conversation is read in. (Catalog: Conversation › MessageStream — the scrolling list of messages, with its days, its unread line and its grouping.)
 *
 * The core piece of the set. It is handed messages and draws them in order, and everything it adds
 * is a statement about the SEQUENCE, which is the one thing a single message cannot know:
 *
 *   • a divider at each change of day, saying Today, Yesterday, then a date;
 *   • a line marking where the reader stopped last time;
 *   • grouping — consecutive messages from one person drawn as one block;
 *   • the scroll behaviour that makes a conversation readable while it is still arriving.
 *
 * ── SCROLLING, which is the part that is easy to get wrong ───────────────────────────────────
 * A new message scrolls the list to the bottom ONLY if the reader was already near it. Somebody
 * reading back through yesterday does not want the view yanked away every time a colleague types,
 * and a list that does that is one people stop scrolling back in. When they are not near the
 * bottom, a control appears offering to take them there — so the message is never merely lost.
 *
 * Both behaviours read ONE threshold, `NEAR_BOTTOM_PX`, from the shared conversation code. Two
 * constants would eventually differ, and the gap between them is a band where an arriving message
 * neither scrolls into view nor offers a way to reach it.
 *
 * ── The unread line is fixed on arrival ──────────────────────────────────────────────────────
 * `unread-since` is read ONCE per conversation, when the conversation opens, and the line stays
 * where it was put. A line recomputed as messages arrive walks down the page ahead of the reader
 * and marks nothing. That is why the conversation's identity is an input (`key`): it is what tells
 * this element that a DIFFERENT conversation is now being read and the line may move.
 *
 * WHEN it stops moving is a separate question from where it goes, and the answer is neither "the
 * first render" nor "the first draw". Attributes arrive one at a time, each firing its own render:
 * a line frozen on the first render carrying a new `key` is frozen before `unread-since` has been
 * set and reads the mark as absent, drawing no line at all — and a change of `key` re-renders with
 * the PREVIOUS conversation's mark still sitting on the attribute, which freezing at draw time
 * would consume.
 *
 * It stops moving once this conversation's own MESSAGES have arrived. That is the one input a
 * surface cannot hand over for a conversation it has not named, so it is the only unambiguous
 * "this one is open now". Until then the mark is simply read from the attribute on every render,
 * which is what lets the two inputs arrive in either order, in any number of separate changes.
 * Afterwards it is frozen, which is the whole behaviour being asked for.
 *
 * "Arrived" means a NON-EMPTY list. "Clear the list, then load it" is an ordinary way to switch
 * conversation, and an empty list has nothing in it for a line to sit above — counting it would
 * freeze the mark against the previous conversation's, before this one's had been set.
 *
 * With one refusal in the middle. Between a change of `key` and the arrival of that conversation's
 * messages, the list still holds the PREVIOUS conversation's content — the surface has named a
 * different conversation and not yet handed over what is in it. A stream in that state has nothing
 * true to say about where this reader stopped, so it says nothing: the messages already on screen
 * stay, because the surface put them there, and NO line is drawn over them. Tracking the attribute
 * there would move a line through messages it does not describe, which is the walking line again
 * wearing a different hat.
 *
 * ── What it does not do ──────────────────────────────────────────────────────────────────────
 * No header, no composer, no channel title, no menu. A stream is the list; the surface around it
 * owns the rest and can put a composer under it and an indicator in the `pending` slot.
 *
 * Styling seams: `::part(scroller)`, `::part(divider)`, `::part(unread)`, `::part(jump)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { continuesPrevious, dayKey, dayLabel, isNearBottom } from '../message-format.js';
import './message-bubble.js';
import type { CaosMessageBubble } from './message-bubble.js';

/** One message, as a surface hands it in. */
export interface CaosMessage {
  /** Stable identity — what a highlight addresses and what keeps a redraw from losing the place. */
  id: string;
  author: string;
  /** ISO-8601. */
  at: string;
  /** Markdown. */
  body: string;
  /** Two characters for the avatar, when the initials taken from the author are not wanted. */
  avatar?: string;
}

const sheet = makeSheet(`
  :host { display: flex; flex-direction: column; min-height: 0; position: relative; }

  .scroller {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: var(--caos-space-2) 0;
  }

  .divider {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: var(--caos-space-2) var(--caos-space-3);
    color: var(--caos-color-text-subtle);
    font-family: var(--caos-font);
    font-size: var(--caos-text-label);
    font-weight: var(--caos-font-weight-label);
  }
  .divider::before, .divider::after {
    content: "";
    flex: 1 1 auto;
    border-top: 1px solid var(--caos-color-border);
  }

  .unread { color: var(--caos-color-accent); }
  .unread::before, .unread::after { border-top-color: var(--caos-color-accent); }

  .jump {
    position: absolute;
    inset-inline-end: var(--caos-space-3);
    inset-block-end: var(--caos-space-3);
    appearance: none;
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-label);
    padding: 6px var(--caos-space-3);
    border-radius: var(--caos-radius-pill);
    cursor: pointer;
    box-shadow: var(--caos-shadow-float);
  }
  .jump:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .empty {
    padding: var(--caos-space-4) var(--caos-space-3);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    text-align: center;
  }
`);

export class CaosMessageStream extends CaosElement {
  static get observedAttributes(): string[] {
    return ['key', 'unread-since', 'highlight', 'empty-label', 'label'];
  }

  private readonly scroller: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly jump: HTMLButtonElement;
  private _messages: CaosMessage[] = [];
  private _absentPeople: string[] = [];
  /** The conversation the unread line was fixed for, and where it was fixed. */
  private anchoredKey: string | null = null;
  private anchoredUnreadSince: string | null = null;
  /** Whether the line has stopped moving — see the header; `null` is also a fixed answer. */
  private unreadFrozen = false;
  /** Whether THIS conversation's messages have been handed over, which is what freezes it. */
  private messagesArrivedForThisKey = false;

  constructor() {
    super(sheet);
    this.scroller = document.createElement('div');
    this.scroller.className = 'scroller';
    this.scroller.setAttribute('part', 'scroller');
    this.scroller.addEventListener('scroll', () => this.reflectJump(), { passive: true });

    this.list = document.createElement('div');
    const pending = document.createElement('slot');
    pending.name = 'pending';
    this.scroller.append(this.list, pending);

    this.jump = document.createElement('button');
    this.jump.type = 'button';
    this.jump.className = 'jump';
    this.jump.setAttribute('part', 'jump');
    this.jump.textContent = 'Jump to latest';
    this.jump.hidden = true;
    this.jump.addEventListener('click', () => this.scrollToLatest());

    this.root.append(this.scroller, this.jump);
  }

  connectedCallback(): void {
    // A log role: a region whose content is appended to, announced as additions rather than as a
    // replaced region. `polite`, because a message arriving must not interrupt a sentence in
    // progress — the reader is reading the conversation, and cutting them off to read them more of
    // it is the worst possible trade.
    if (!this.hasAttribute('role')) this.setAttribute('role', 'log');
    if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
    this.render();
  }

  attributeChangedCallback(name: string, before: string | null, after: string | null): void {
    if (before === after) return;
    if (name === 'highlight') {
      this.reflectHighlight();
      return;
    }
    this.render();
  }

  /**
   * The conversation being read. Changing it is what lets the unread line move — see the header.
   * A stream with no key behaves as one conversation that never changes.
   */
  get key(): string {
    return this.getAttribute('key') ?? '';
  }
  set key(value: string) {
    this.setAttribute('key', value);
  }

  /** The messages, oldest first. Ordering is the caller's: this draws what it is given, in order. */
  get messages(): CaosMessage[] {
    return this._messages;
  }
  set messages(list: readonly CaosMessage[]) {
    const wasNearBottom = this.isFollowing();
    this._messages = [...list];
    // The conversation named by `key` is now open — see the header. The render below reads the
    // mark one last time and then freezes it. An EMPTY list is not an opening: "clear the list,
    // then load it" is an ordinary way to switch conversation, and counting the clearing would
    // freeze the mark against the previous conversation's, before this one's had arrived.
    if (this._messages.length > 0) this.messagesArrivedForThisKey = true;
    this.render();
    // Only follow the conversation for a reader who was already following it.
    if (wasNearBottom) this.scrollToLatest();
    else this.reflectJump();
  }

  /**
   * Principal ids of mentioned people who are no longer in this workspace (CAOS-455).
   *
   * Forwarded to every bubble rather than interpreted here — this component knows about order, days
   * and the unread line, and nothing about what is inside a body. Set after the messages are in, or
   * before: both work, and the later one is the ordinary case because the surface has to ask over
   * the wire who has left.
   */
  get absentPeople(): readonly string[] {
    return this._absentPeople;
  }
  set absentPeople(value: Iterable<string>) {
    this._absentPeople = [...(value ?? [])];
    this.render();
  }

  /** The instant the reader last saw this conversation. Messages after it sit below the line. */
  get unreadSince(): string {
    return this.getAttribute('unread-since') ?? '';
  }
  set unreadSince(value: string) {
    this.setAttribute('unread-since', value);
  }

  /** Which message was navigated to, by id. */
  get highlight(): string {
    return this.getAttribute('highlight') ?? '';
  }
  set highlight(value: string) {
    this.setAttribute('highlight', value);
  }

  /** Is the reader at the living end of the conversation? */
  isFollowing(): boolean {
    return isNearBottom(this.scroller);
  }

  /** Take the reader to the newest message. */
  scrollToLatest(): void {
    this.scroller.scrollTop = this.scroller.scrollHeight;
    this.reflectJump();
  }

  /** Bring one message into view and mark it. Returns false when this stream does not hold it. */
  reveal(id: string): boolean {
    const el = this.list.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(id)}"]`);
    if (!el) return false;
    this.highlight = id;
    el.scrollIntoView({ block: 'center' });
    return true;
  }

  /**
   * Read the mark, and freeze it once this conversation's messages are in hand. See the header:
   * after that a later `unread-since` is ignored for as long as the conversation is the same one,
   * because a line that follows the mark walks down the page ahead of the reader.
   */
  private fixUnreadLine(): void {
    if (this.unreadFrozen) return;
    // The messages on screen belong to the conversation before this one — see the header. Nothing
    // true can be said about this reader's place in a conversation whose messages are not here.
    if (!this.messagesArrivedForThisKey && this._messages.length > 0) return;
    this.anchoredUnreadSince = this.unreadSince || null;
    if (this.messagesArrivedForThisKey) this.unreadFrozen = true;
  }

  /** A different conversation is being read, so the line it had no longer means anything. */
  private forgetUnreadLineIfConversationChanged(): void {
    if (this.anchoredKey === this.key) return;
    this.anchoredKey = this.key;
    this.anchoredUnreadSince = null;
    this.unreadFrozen = false;
    // The messages on screen belong to the conversation before this one, so nothing has arrived
    // for this one yet and the mark is free to move again until something does.
    this.messagesArrivedForThisKey = false;
  }

  private render(): void {
    // A change of conversation clears the line: the previous one's must not be left standing over
    // this one.
    this.forgetUnreadLineIfConversationChanged();
    this.fixUnreadLine();

    this.list.replaceChildren();
    if (this._messages.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.setAttribute('part', 'empty');
      empty.textContent = this.getAttribute('empty-label') ?? 'No messages yet.';
      this.list.append(empty);
      this.jump.hidden = true;
      return;
    }

    const now = new Date();
    const unread = this.anchoredUnreadSince ? new Date(this.anchoredUnreadSince) : null;
    const unreadValid = unread !== null && !Number.isNaN(unread.getTime());
    let unreadDrawn = false;
    let previous: { author: string; at: Date } | null = null;
    let previousDay: string | null = null;

    for (const message of this._messages) {
      const at = new Date(message.at);
      const valid = !Number.isNaN(at.getTime());
      const day = valid ? dayKey(at) : null;

      let broken = false;
      if (valid && day !== previousDay) {
        this.list.append(this.dividerNode(dayLabel(at, now), false));
        previousDay = day;
        broken = true;
      }
      if (unreadValid && !unreadDrawn && valid && unread && at.getTime() > unread.getTime()) {
        this.list.append(this.dividerNode('New messages', true));
        unreadDrawn = true;
        broken = true;
      }

      const bubble = document.createElement('caos-message-bubble') as CaosMessageBubble;
      bubble.dataset['messageId'] = message.id;
      bubble.author = message.author;
      bubble.at = message.at;
      if (message.avatar !== undefined) bubble.setAttribute('avatar', message.avatar);
      // Forwarded, never interpreted: who has left is the surface's answer, not this component's.
      // BEFORE the body, so the body is drawn once with the marks already in place. After it, the
      // `body` setter has already rendered and this setter renders the same body again.
      bubble.absentPeople = this._absentPeople;
      bubble.body = message.body;
      // A divider between two messages and those two drawn as one block are contradictory
      // statements about the same pair, so anything that broke the run breaks the grouping too.
      bubble.grouped = !broken && valid && previous !== null && continuesPrevious(previous, { author: message.author, at });
      this.list.append(bubble);

      if (valid) previous = { author: message.author, at };
      else previous = null;
    }

    this.reflectHighlight();
    this.reflectJump();
  }

  private dividerNode(text: string, unread: boolean): HTMLElement {
    const div = document.createElement('div');
    div.className = unread ? 'divider unread' : 'divider';
    div.setAttribute('part', unread ? 'unread' : 'divider');
    // A separator, so a screen reader announces the break rather than reading the word as if it
    // were part of the message under it.
    div.setAttribute('role', 'separator');
    div.setAttribute('aria-label', text);
    div.textContent = text;
    return div;
  }

  private reflectHighlight(): void {
    const id = this.highlight;
    for (const el of Array.from(this.list.querySelectorAll<HTMLElement>('caos-message-bubble'))) {
      el.toggleAttribute('highlighted', el.dataset['messageId'] === id && id !== '');
    }
  }

  private reflectJump(): void {
    this.jump.hidden = this._messages.length === 0 || this.isFollowing();
  }
}

define('caos-message-stream', CaosMessageStream);

declare global {
  interface HTMLElementTagNameMap {
    'caos-message-stream': CaosMessageStream;
  }
}
